export const RegistrationStates = {
  CREATED: "created",
  UNPAID_PREREG_FEE: "unpaidPreregistrationFee",
  AWAITING_MEMBERS: "awaitingMembers",
  PREREGISTERED: "preregistered",
  CONFIRMED: "confirmed",
  ON_START_LIST: "onStartList",
  OVERDUE: "overdue",
  UNPAID: "unpaid",
  CANCELLED: "cancelled",
  UNDRAWN: "undrawn",
  PENDING_MARKET: "pendingMarket",
  SOLD: "sold",
  MARKET_PENDING_DATA: "marketPendingData",
};

export const RegistrationStateLabels = {
  [RegistrationStates.CREATED]: "Nedokončena",
  [RegistrationStates.UNPAID_PREREG_FEE]: "Nezaplacená předregistrace",
  [RegistrationStates.AWAITING_MEMBERS]: "Čeká na členy",
  [RegistrationStates.PREREGISTERED]: "Předregistrována",
  [RegistrationStates.CONFIRMED]: "Čeká na platbu",
  [RegistrationStates.ON_START_LIST]: "Na startovce",
  [RegistrationStates.OVERDUE]: "Po splatnosti",
  [RegistrationStates.UNPAID]: "Nezaplaceno",
  [RegistrationStates.CANCELLED]: "Zrušena",
  [RegistrationStates.UNDRAWN]: "Nevylosována",
  [RegistrationStates.PENDING_MARKET]: "Na tržišti",
  [RegistrationStates.SOLD]: "Prodáno",
  [RegistrationStates.MARKET_PENDING_DATA]: "Vyžaduje doplnění údajů",
};

/**
 * Registration states where adding a voucher is supported (`/registration/addVoucher`).
 * Keep in sync with `b7-app-main/app/routes/registration/add-voucher.js`.
 */
export const REGISTRATION_STATES_ELIGIBLE_FOR_VOUCHER = Object.freeze([
  RegistrationStates.CREATED,
  RegistrationStates.CANCELLED,
  RegistrationStates.UNPAID_PREREG_FEE,
  RegistrationStates.PREREGISTERED,
  RegistrationStates.UNDRAWN,
  RegistrationStates.UNPAID,
]);

export const InvoiceStates = {
  PAID: { value: "paid", label: "Zaplaceno" },
  OVERDUE: { value: "overdue", label: "Po splatnosti" },
};

export const InvoiceStateLabels = Object.fromEntries(
  Object.values(InvoiceStates).map((state) => [state.value, state.label]),
);

export const RegistrationFeeStates = {
  PAID: { value: true, label: "Zaplaceno" },
  NOT_PAID: { value: false, label: "Nezaplaceno" },
};

export const RegistrationFeeStateLabels = Object.fromEntries(
  Object.values(RegistrationFeeStates).map((state) => [state.value, state.label]),
);

/** Fields for RaceConfig registrations list: grid, stats panel, and CSV export (export-only fields are not grid columns). */
export const REGISTRATION_LIST_PROJECTION_RACE_CONFIG_WITH_STATS = Object.freeze({
  _id: 1,
  userId: 1,
  teamId: 1,
  firstName: 1,
  surname: 1,
  birthdate: 1,
  birthNumber: 1,
  email: 1,
  phone: 1,
  master: 1,
  voucher: 1,
  state: 1,
  createdAt: 1,
  registrationFee: 1,
  registrationPayment: 1,
  category: 1,
  sex: 1,
  nationality: 1,
  insuranceCompany: 1,
  club: 1,
  street: 1,
  streetNumber: 1,
  city: 1,
  zip: 1,
  country: 1,
  tshirtSize: 1,
  extraMerchandise: 1,
  startNumber: 1,
  accreditation: 1,
});

/** Same as {@link REGISTRATION_LIST_PROJECTION_RACE_CONFIG_WITH_STATS} without stats-only fields (category, sex) */
export const REGISTRATION_LIST_PROJECTION_RACE_CONFIG_GRID_ONLY = Object.freeze({
  _id: 1,
  userId: 1,
  teamId: 1,
  firstName: 1,
  surname: 1,
  birthdate: 1,
  birthNumber: 1,
  email: 1,
  phone: 1,
  master: 1,
  voucher: 1,
  state: 1,
  createdAt: 1,
  registrationFee: 1,
  registrationPayment: 1,
  nationality: 1,
  insuranceCompany: 1,
  club: 1,
  street: 1,
  streetNumber: 1,
  city: 1,
  zip: 1,
  country: 1,
  tshirtSize: 1,
  extraMerchandise: 1,
  startNumber: 1,
  accreditation: 1,
});

/** Lottery tab table + summary (see LotteryTab.jsx, LotterySummary.jsx, LotteryRegistrationsTable.jsx) */
export const REGISTRATION_LIST_PROJECTION_LOTTERY_ONE = Object.freeze({
  _id: 1,
  userId: 1,
  firstName: 1,
  surname: 1,
  birthdate: 1,
  state: 1,
  lotteryOneResult: 1,
});

export const REGISTRATION_LIST_PROJECTION_LOTTERY_TWO = Object.freeze({
  _id: 1,
  userId: 1,
  firstName: 1,
  surname: 1,
  birthdate: 1,
  state: 1,
  lotteryTwoResult: 1,
});

export const VoucherErrorStates = {
  NOT_FOUND: "notFound",
  INACTIVE: "inactive",
  USED: "used",
};

export const InsuranceCompanies = [
  { code: "111", label: "111 - Všeobecná zdravotní pojišťovna ČR" },
  { code: "201", label: "201 - Vojenská zdravotní pojišťovna ČR" },
  { code: "205", label: "205 - Česká průmyslová zdravotní pojišťovna" },
  { code: "207", label: "207 - Oborová zdravotní pojišťovna zaměstnanců bank, pojišťoven a stavebnictví" },
  { code: "209", label: "209 - Zaměstnanecká pojišťovna ŠKODA" },
  { code: "211", label: "211 - Zdravotní pojišťovna Ministerstva vnitra ČR" },
  { code: "213", label: "213 - Revírní bratrská pokladna, zdravotní pojišťovna" },
  { code: "217", label: "217 - Zdravotní pojišťovna METAL-ALIANCE" },
  { code: "222", label: "222 - Česká národní zdravotní pojišťovna" },
  { code: "227", label: "227 - Zdravotní pojišťovna AGEL" },
  { code: "228", label: "228 - Zdravotní pojišťovna MÉDIA" },
  { code: "300", label: "300 - Samoplátce" },
  { code: "500", label: "500 - Vlastní cestovní pojištení" },
];

export const TShirtSizes = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];

export const PhoneCountryCodes = [
  "+420", // Česká republika
  "+421", // Slovensko
  "+1", // USA, Kanada
  "+44", // Spojené království
  "+49", // Německo
  "+33", // Francie
  "+39", // Itálie
  "+34", // Španělsko
  "+31", // Nizozemsko
  "+32", // Belgie
  "+41", // Švýcarsko
  "+43", // Rakousko
  "+45", // Dánsko
  "+46", // Švédsko
  "+47", // Norsko
  "+48", // Polsko
  "+351", // Portugalsko
  "+352", // Lucembursko
  "+353", // Irsko
  "+30", // Řecko
  "+358", // Finsko
  "+372", // Estonsko
  "+371", // Lotyšsko
  "+370", // Litva
  "+36", // Maďarsko
  "+40", // Rumunsko
  "+359", // Bulharsko
  "+385", // Chorvatsko
  "+386", // Slovinsko
  "+387", // Bosna a Hercegovina
  "+382", // Černá Hora
  "+381", // Srbsko
  "+389", // Makedonie
  "+355", // Albánie
  "+7", // Rusko, Kazachstán
  "+81", // Japonsko
  "+82", // Jižní Korea
  "+86", // Čína
  "+91", // Indie
  "+61", // Austrálie
  "+64", // Nový Zéland
  "+55", // Brazílie
  "+52", // Mexiko
  "+54", // Argentina
  "+27", // Jihoafrická republika
  "+20", // Egypt
  "+971", // Spojené arabské emiráty
  "+966", // Saúdská Arábie
  "+972", // Izrael
  "+90", // Turecko
];

/**
 * Field name to Czech label mapping for registration form fields
 * Used for consistent labeling across the application and error messages
 */
export const RegistrationFieldLabels = {
  sex: "Pohlaví",
  phone: "Telefon",
  nationality: "Národnost",
  birthNumber: "Rodné číslo",
  birthdate: "Datum narození",
  insuranceCompany: "Zdravotní pojišťovna",
  club: "Název týmu / klubu",
  street: "Ulice",
  streetNumber: "Číslo popisné",
  city: "Město",
  zip: "PSČ",
  country: "Země",
  tshirtSize: "Velikost trička",
};

/**
 * Mapping from Czech country names to ISO 3166-1 alpha-2 codes
 * Used to convert country names to ISO codes for backend storage
 * The keys of this object represent all available countries
 */
export const CountryNameToISO = {
  "Česká republika": "CZ",
  Slovensko: "SK",
  Polsko: "PL",
  Afghánistán: "AF",
  Albánie: "AL",
  Alžírsko: "DZ",
  Andorra: "AD",
  Angola: "AO",
  "Antigua a Barbuda": "AG",
  Argentina: "AR",
  Arménie: "AM",
  Austrálie: "AU",
  Ázerbájdžán: "AZ",
  Bahamy: "BS",
  Bahrajn: "BH",
  Bangladéš: "BD",
  Barbados: "BB",
  Belgie: "BE",
  Belize: "BZ",
  Benin: "BJ",
  Bhútán: "BT",
  Bolívie: "BO",
  "Bosna a Hercegovina": "BA",
  Botswana: "BW",
  Brazílie: "BR",
  Brunej: "BN",
  Bulharsko: "BG",
  "Burkina Faso": "BF",
  Burundi: "BI",
  Čad: "TD",
  "Černá Hora": "ME",
  Čína: "CN",
  Dánsko: "DK",
  Dominika: "DM",
  "Dominikánská republika": "DO",
  Džibutsko: "DJ",
  Egypt: "EG",
  Ekvádor: "EC",
  Eritrea: "ER",
  Estonsko: "EE",
  Etiopie: "ET",
  Fidži: "FJ",
  Filipíny: "PH",
  Finsko: "FI",
  Francie: "FR",
  Gabon: "GA",
  Gambie: "GM",
  Ghana: "GH",
  Gruzie: "GE",
  Guatemala: "GT",
  Guinea: "GN",
  "Guinea-Bissau": "GW",
  Guyana: "GY",
  Haiti: "HT",
  Honduras: "HN",
  Chile: "CL",
  Chorvatsko: "HR",
  Indie: "IN",
  Indonésie: "ID",
  Irák: "IQ",
  Írán: "IR",
  Irsko: "IE",
  Island: "IS",
  Izrael: "IL",
  Itálie: "IT",
  Jamajka: "JM",
  Japonsko: "JP",
  Jemen: "YE",
  "Jihoafrická republika": "ZA",
  "Jižní Korea": "KR",
  "Jižní Súdán": "SS",
  Jordánsko: "JO",
  Kambodža: "KH",
  Kamerun: "CM",
  Kanada: "CA",
  Kapverdy: "CV",
  Katar: "QA",
  Kazachstán: "KZ",
  Keňa: "KE",
  Kiribati: "KI",
  Kolumbie: "CO",
  Komory: "KM",
  Kongo: "CG",
  Kostarika: "CR",
  Kuba: "CU",
  Kuvajt: "KW",
  Kypr: "CY",
  Kyrgyzstán: "KG",
  Laos: "LA",
  Lesotho: "LS",
  Libanon: "LB",
  Libérie: "LR",
  Libye: "LY",
  Lichtenštejnsko: "LI",
  Litva: "LT",
  Lotyšsko: "LV",
  Lucembursko: "LU",
  Madagaskar: "MG",
  Maďarsko: "HU",
  Makedonie: "MK",
  Malajsie: "MY",
  Malawi: "MW",
  Maledivy: "MV",
  Mali: "ML",
  Malta: "MT",
  Maroko: "MA",
  "Marshallovy ostrovy": "MH",
  Mauritánie: "MR",
  Mauricius: "MU",
  Mexiko: "MX",
  Mikronésie: "FM",
  Moldavsko: "MD",
  Monako: "MC",
  Mongolsko: "MN",
  Mosambik: "MZ",
  Myanmar: "MM",
  Namibie: "NA",
  Nauru: "NR",
  Německo: "DE",
  Nepál: "NP",
  Niger: "NE",
  Nigérie: "NG",
  Nikaragua: "NI",
  Nizozemsko: "NL",
  Norsko: "NO",
  "Nový Zéland": "NZ",
  Omán: "OM",
  Pákistán: "PK",
  Palau: "PW",
  Panama: "PA",
  "Papua-Nová Guinea": "PG",
  Paraguay: "PY",
  Peru: "PE",
  "Pobřeží slonoviny": "CI",
  Portugalsko: "PT",
  Rakousko: "AT",
  "Rovníková Guinea": "GQ",
  Rumunsko: "RO",
  Rusko: "RU",
  Rwanda: "RW",
  Řecko: "GR",
  Salvador: "SV",
  Samoa: "WS",
  "San Marino": "SM",
  "Saúdská Arábie": "SA",
  Senegal: "SN",
  "Severní Korea": "KP",
  Seychely: "SC",
  "Sierra Leone": "SL",
  Singapur: "SG",
  Slovinsko: "SI",
  Somálsko: "SO",
  "Spojené arabské emiráty": "AE",
  "Spojené království": "GB",
  "Spojené státy americké": "US",
  Srbsko: "RS",
  "Středoafrická republika": "CF",
  Súdán: "SD",
  Surinam: "SR",
  "Svatá Lucie": "LC",
  "Svatý Kryštof a Nevis": "KN",
  "Svatý Tomáš a Princův ostrov": "ST",
  "Svatý Vincenc a Grenadiny": "VC",
  Svazijsko: "SZ",
  Španělsko: "ES",
  Švédsko: "SE",
  Švýcarsko: "CH",
  Sýrie: "SY",
  Tádžikistán: "TJ",
  Tanzanie: "TZ",
  Thajsko: "TH",
  Togo: "TG",
  Tonga: "TO",
  "Trinidad a Tobago": "TT",
  Tunisko: "TN",
  Turecko: "TR",
  Turkmenistán: "TM",
  Tuvalu: "TV",
  Uganda: "UG",
  Ukrajina: "UA",
  Uruguay: "UY",
  Uzbekistán: "UZ",
  Vanuatu: "VU",
  Vatikán: "VA",
  Venezuela: "VE",
  Vietnam: "VN",
  "Východní Timor": "TL",
  Zambie: "ZM",
  Zimbabwe: "ZW",
};

/**
 * Reverse mapping from ISO codes to Czech country names
 * Used to convert ISO codes back to country names for display
 */
export const ISOToCountryName = Object.fromEntries(Object.entries(CountryNameToISO).map(([name, iso]) => [iso, name]));

/**
 * Array of all available country names
 * Derived from CountryNameToISO keys
 */
export const Countries = Object.keys(CountryNameToISO);
